import * as queryString from 'query-string';
import React, { ReactNode, useEffect, useState } from 'react';

import EntityIcon from '@sbu/assets.svg/src/entities.svg';
import SingleChevronDownIcon from '@sbu/assets.svg/src/single-chevron-down.svg';
import {
  DataGridAside,
  DataGridAsideTitle,
} from '@sbu/components.datagridaside';
import { EmptyMessage } from '@sbu/components.emptymessage';
import { Link } from '@sbu/components.link';
import { Toggle } from '@sbu/components.toggle';
import { useToasts } from '@sbu/contexts.toasts';
import { usePermission } from '@sbu/hooks.permissions';
import { getSignalSeverityStatusColor } from '@sbu/utils.signals';
import { EventPropertyName, TrackEventName } from '@sbu/utils.tracking';
import {
  EntityIndicator,
  EntityIndicatorReputation,
} from '@sbu/views.entityindicator';
import { Tags } from '@sbu/views.tags';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  SvgIcon,
  Typography,
  useTheme,
} from '@sumologic/yama-ds-components';

import {
  EntityListFragment,
  UpdateEntityInputCriticalityDocument,
  UpdateEntityInputCriticalityMutation,
  UpdateEntityInputCriticalityMutationVariables,
  UpdateEntitySuppressionMutation,
  UpdateEntitySuppressionMutationVariables,
} from 'core/graphql';
import { SortDirection } from 'shared/components/data/Sort';
import { EntitySuppressLists } from 'shared/components/internal/EntityAside/EntitySuppressLists';
import { SortProps } from 'shared/components/internal/SortMenu/sortOptions/entity';
import { useMutation, useTrackEvent } from 'shared/hooks';
import { serializeSort } from 'shared/utils/query';

import { EntitiesCriticalityEditor } from '../EntitiesBulkActions/EntitiesCriticalityEditor';
import { EntitiesSuppressionEditor } from '../EntitiesBulkActions/EntitiesSuppressionEditor';
import { EntitiesTagEditor } from '../EntitiesBulkActions/EntitiesTagEditor';
import { UPDATE_ENTITY_SUPPRESSION_MUTATION } from '../mutations';
import { useEntityCriticalities } from '../queries';
import { useEntitiesContext } from './EntitiesContextProvider';
import { getSelectedRowsLabel, OBJECT_TYPE } from './utils';

interface ReferrerProps {
  offset: number;
  q: string;
  sorts: SortDirection<SortProps>[];
}

interface EntitiesDialogState {
  criticalityMenuAnchorEl: HTMLElement | null;
  criticalityDialogOptions: {
    isOpen: boolean;
    criticality: string | null;
  };
}

interface EntitiesDataGridAsideProps {
  insightConfigThreshold?: number | null | undefined;
  referrerProps: ReferrerProps;
  onClose: () => void;
}

const useEntitiesAside = () => {
  const hasManagePermission = usePermission('can_manage_entity');
  const { data: criticalities, loading } = useEntityCriticalities();

  const [dialogState, setDialogState] = useState<EntitiesDialogState>({
    criticalityMenuAnchorEl: null,
    criticalityDialogOptions: {
      isOpen: false,
      criticality: null,
    },
  });

  const handleCriticalityMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDialogState({
      ...dialogState,
      criticalityMenuAnchorEl: event.currentTarget,
    });
  };

  const handleCriticalityMenuClose = () => {
    setDialogState({
      ...dialogState,
      criticalityMenuAnchorEl: null,
    });
  };

  const handleCriticalityChange = (criticality: string) => {
    setDialogState({
      ...dialogState,
      criticalityMenuAnchorEl: null,
      criticalityDialogOptions: {
        isOpen: true,
        criticality,
      },
    });
  };

  const handleCriticalityDialogClose = () => {
    setDialogState({
      ...dialogState,
      criticalityDialogOptions: {
        isOpen: false,
        criticality: null,
      },
    });
  };

  return {
    hasManagePermission,
    criticalityLoading: loading,
    criticalities,
    dialogState,
    handleCriticalityMenuOpen,
    handleCriticalityMenuClose,
    handleCriticalityChange,
    handleCriticalityDialogClose,
    handleCriticalityDialogCancel: handleCriticalityDialogClose,
  };
};

const SingleEntityView = ({
  insightConfigThreshold,
  referrerProps: { offset, q, sorts },
  onClose,
  entity,
}: EntitiesDataGridAsideProps & { entity: EntityListFragment }) => {
  const [suppressed, setSuppressed] = useState<boolean>(
    entity?.isWhitelisted ?? false
  );
  const [criticality, setCriticality] = useState<string | null>(
    entity?.criticality ?? null
  );

  const {
    hasManagePermission,
    criticalityLoading,
    criticalities,
    dialogState,
    handleCriticalityMenuOpen,
    handleCriticalityMenuClose,
    handleCriticalityChange,
    handleCriticalityDialogClose,
    handleCriticalityDialogCancel,
  } = useEntitiesAside();

  const trackEvent = useTrackEvent();

  const { addSuccess } = useToasts();

  const [
    updateEntitySuppression,
    { loading: updatingSuppression, data: updatedEntitySuppressionData },
  ] = useMutation<
    UpdateEntitySuppressionMutation,
    UpdateEntitySuppressionMutationVariables
  >(UPDATE_ENTITY_SUPPRESSION_MUTATION, {
    onCompleted: () => {
      addSuccess('Entity suppression updated successfully');
    },
  });

  const [
    updateEntityCriticality,
    { loading: updatingCriticality, data: updatedEntityCriticalityData },
  ] = useMutation<
    UpdateEntityInputCriticalityMutation,
    UpdateEntityInputCriticalityMutationVariables
  >(UpdateEntityInputCriticalityDocument, {
    onCompleted: () => {
      addSuccess('Entity criticality updated successfully');
    },
  });

  useEffect(() => {
    setSuppressed(entity?.isWhitelisted ?? false);
    setCriticality(entity?.criticality ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.id]);

  useEffect(() => {
    if (
      !updatingSuppression &&
      updatedEntitySuppressionData?.updateEntityWhitelist?.data
        ?.isWhitelisted !== undefined
    ) {
      setSuppressed(
        updatedEntitySuppressionData.updateEntityWhitelist.data.isWhitelisted
      );
    }
  }, [updatingSuppression, updatedEntitySuppressionData]);

  useEffect(() => {
    if (
      !updatingCriticality &&
      updatedEntityCriticalityData?.updateEntityCriticality?.data
        ?.criticality !== undefined
    ) {
      setCriticality(
        updatedEntityCriticalityData.updateEntityCriticality.data.criticality
      );
    }
  }, [updatingCriticality, updatedEntityCriticalityData]);

  const renderField = (label: string, value: string | ReactNode) => {
    return (
      <Stack alignItems='center' direction='row' gap={1}>
        <Typography color='text.secondary' variant='body2' width={112}>
          {label}
        </Typography>
        {typeof value === 'string' ? <Typography>{value}</Typography> : value}
      </Stack>
    );
  };

  const selectedRowsLabel = getSelectedRowsLabel(entity, undefined);

  return (
    <>
      <DataGridAside
        data-sumo-test='entities-aside'
        onClose={onClose}
        variant='overlay'
      >
        <DataGridAsideTitle
          state='read'
          row={entity}
          getObjectName={(data) => `${data.entityTypeName}: ${data.value}`}
          objectType={OBJECT_TYPE}
        />
        {entity.reputation && (
          <Box data-sumo-test='entity-indicator' mx={2} mb={3}>
            <EntityIndicator
              reputation={entity.reputation as EntityIndicatorReputation}
            />
          </Box>
        )}
        <Stack
          direction='row'
          gap={1}
          justifyContent='flex-start'
          px={2}
          mb={2}
        >
          <Button
            LinkComponent={Link}
            href={`/sec/entity?entityId=${encodeURIComponent(
              entity.id
            )}&${queryString.stringify({
              offset,
              q,
              sorts: serializeSort(sorts as SortDirection[]),
            })}`}
            size='small'
            variant='outlined'
            onClick={() =>
              trackEvent(TrackEventName.VIEW_ENTITY_DETAILS, {
                [EventPropertyName.ORIGIN]: 'SidePanelClick',
                [EventPropertyName.ENTITY_ID]: entity.id,
              })
            }
          >
            Open
          </Button>
          {hasManagePermission &&
            criticalities?.entityCriticalityConfigs?.objects?.length && (
              <>
                <Button
                  size='small'
                  color='secondary'
                  loading={criticalityLoading}
                  onClick={handleCriticalityMenuOpen}
                  endIcon={<SingleChevronDownIcon height={12} width={12} />}
                >
                  Change Criticality
                </Button>
                <Menu
                  anchorEl={dialogState.criticalityMenuAnchorEl}
                  onClose={handleCriticalityMenuClose}
                  open={Boolean(dialogState.criticalityMenuAnchorEl)}
                >
                  <MenuList>
                    {criticalities?.entityCriticalityConfigs.objects.map(
                      (entityCriticality) => (
                        <MenuItem
                          key={entityCriticality.id}
                          onClick={() =>
                            handleCriticalityChange(entityCriticality.name)
                          }
                        >
                          <ListItemText
                            primary={entityCriticality.name}
                            secondary={entityCriticality.severityExpression}
                          />
                        </MenuItem>
                      )
                    )}
                  </MenuList>
                </Menu>
              </>
            )}
        </Stack>
        <Box>
          <Accordion defaultExpanded>
            <AccordionSummary>
              <Typography>Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack gap={1}>
                {renderField(
                  'Activity Score',
                  <Typography>{entity.activityScore ?? '-'}</Typography>
                )}
                {renderField(
                  'Signal Severity Total',
                  <Typography
                    color={getSignalSeverityStatusColor(
                      entity.recentSignalSeverity,
                      insightConfigThreshold
                    )}
                  >
                    {entity.recentSignalSeverity}
                  </Typography>
                )}
                {renderField(
                  'Suppressed Lists',
                  entity.suppressListsObj?.length ? (
                    <EntitySuppressLists
                      suppressListsObj={entity.suppressListsObj}
                      showHeader={false}
                    />
                  ) : (
                    <Typography>-</Typography>
                  )
                )}
                {renderField(
                  'Criticality',
                  <Typography>{criticality ?? 'Default'}</Typography>
                )}
                {renderField(
                  'Suppression',
                  <Toggle
                    label={suppressed ? 'true' : 'false'}
                    disabled={updatingSuppression || !hasManagePermission}
                    SwitchProps={{
                      checked: suppressed,
                      onChange: async (_, checked) => {
                        if (updatingSuppression) {
                          return;
                        }

                        trackEvent(TrackEventName.ENTITY_ACTION, {
                          [EventPropertyName.PAGE]: 'Entities',
                          [EventPropertyName.ENTITY_ID]: entity.id,
                          [EventPropertyName.ACTION]: 'ChangeSuppression',
                        });

                        await updateEntitySuppression({
                          variables: {
                            entityId: entity.id,
                            suppressed: checked,
                          },
                        });
                      },
                    }}
                  />
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary>
              <Typography>Tags</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {entity.tags?.length > 0 ? (
                <Tags tags={entity.tags} />
              ) : (
                <EmptyMessage alignItems='flex-start'>
                  No tags assigned
                </EmptyMessage>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </DataGridAside>
      <Dialog
        open={dialogState.criticalityDialogOptions.isOpen}
        onClose={handleCriticalityDialogClose}
        maxWidth='sm'
        fullWidth={true}
        aria-labelledby='entity-criticality-dialog-title'
      >
        <DialogTitle id='entity-criticality-dialog-title'>{`Change Criticality for ${selectedRowsLabel}`}</DialogTitle>
        <DialogContent data-sumo-test='entity-criticality-dialog-content'>
          This will change criticality for {selectedRowsLabel} to{' '}
          <Typography component='span' variant='subtitle1'>
            {dialogState.criticalityDialogOptions.criticality}
          </Typography>
          . Continue?
        </DialogContent>
        <DialogActions>
          <Button
            color='secondary'
            onClick={handleCriticalityDialogCancel}
            disabled={updatingCriticality}
          >
            Cancel
          </Button>
          <Button
            color='error'
            onClick={async () => {
              if (updatingCriticality) {
                return;
              }

              trackEvent(TrackEventName.ENTITY_ACTION, {
                [EventPropertyName.PAGE]: 'Entities',
                [EventPropertyName.ENTITY_ID]: entity.id,
                [EventPropertyName.ACTION]: 'ChangeCriticality',
              });

              await updateEntityCriticality({
                variables: {
                  entityId: entity.id,
                  criticality:
                    dialogState.criticalityDialogOptions.criticality ===
                    'Default'
                      ? null
                      : dialogState.criticalityDialogOptions.criticality,
                },
              });
              handleCriticalityDialogClose();
            }}
            disabled={updatingCriticality}
            loading={updatingCriticality}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const BulkActionsView = ({
  onClose,
  entities,
}: Omit<
  EntitiesDataGridAsideProps,
  'insightConfigThreshold' | 'referrerProps'
> & {
  entities: EntityListFragment[];
}) => {
  const hasManagePermission = usePermission('can_manage_entity');

  const { palette } = useTheme();

  const [tagEditorVisible, setTagEditorVisible] = useState(false);
  const [suppressionEditorVisible, setSuppressionEditorVisible] =
    useState(false);
  const [criticalityEditorVisible, setCriticalityEditorVisible] =
    useState(false);

  const entityIds = entities.map((entity) => entity.id);
  const selectedRowsLabel = getSelectedRowsLabel(undefined, entities);

  return (
    <>
      <DataGridAside
        data-sumo-test='entities-aside'
        onClose={onClose}
        variant='overlay'
      >
        <Typography
          variant='h2'
          sx={{
            wordWrap: 'break-word',
            px: 2,
            pt: 2,
            mb: 2,
          }}
        >
          {selectedRowsLabel}
        </Typography>
        <Stack
          direction='row'
          gap={1}
          justifyContent='flex-start'
          px={2}
          mb={2}
        >
          {hasManagePermission && (
            <Button
              size='small'
              color='secondary'
              onClick={() => setTagEditorVisible(!tagEditorVisible)}
            >
              Change Tags
            </Button>
          )}
          {hasManagePermission && (
            <Button
              size='small'
              color='secondary'
              onClick={() =>
                setSuppressionEditorVisible(!suppressionEditorVisible)
              }
            >
              Change Suppression
            </Button>
          )}
          {hasManagePermission && (
            <Button
              size='small'
              color='secondary'
              onClick={() =>
                setCriticalityEditorVisible(!criticalityEditorVisible)
              }
            >
              Change Criticality
            </Button>
          )}
        </Stack>
        {entities.map((e, index) => (
          <Stack
            alignItems='center'
            direction='row'
            gap={1.25}
            key={`selected-row-${index}`}
            p={2}
          >
            <SvgIcon
              component={EntityIcon}
              inheritViewBox
              sx={{
                color: palette.tokens.YamaTypographyColorIcon,
                height: 24,
                width: 24,
              }}
            />
            <Typography color='text.primary' variant='h3' m={0}>
              {`${e.entityTypeName}: ${e.value}`}
            </Typography>
          </Stack>
        ))}
      </DataGridAside>
      {tagEditorVisible && (
        <EntitiesTagEditor
          entityIds={entityIds}
          onClose={() => setTagEditorVisible(false)}
        />
      )}
      {suppressionEditorVisible && (
        <EntitiesSuppressionEditor
          entityIds={entityIds}
          onClose={() => setSuppressionEditorVisible(false)}
        />
      )}
      {criticalityEditorVisible && (
        <EntitiesCriticalityEditor
          entityIds={entityIds}
          onClose={() => setCriticalityEditorVisible(false)}
        />
      )}
    </>
  );
};

export const EntitiesDataGridAside = (props: EntitiesDataGridAsideProps) => {
  const { selectedRow, selectedRows } = useEntitiesContext();

  if (!selectedRow && (!selectedRows || selectedRows.length === 0)) {
    return null;
  }

  if (selectedRows && selectedRows.length > 1) {
    return <BulkActionsView {...props} entities={selectedRows} />;
  }

  return <SingleEntityView {...props} entity={selectedRow!} />;
};
